import { MutableRefObject, useMemo, useRef } from 'react';
import { ArrayUtils } from '../../../util/array';
import { QNear, Waypoint } from '../../../util/machine-variable.utils';
import {
  RegisterAndSubscribeToMachineBindingDocument,
  RegisterAndSubscribeToMachineBindingSubscriptionResult,
  RegisterAndSubscribeToMachineBindingSubscriptionVariables,
} from '../../graphql/generated';
import { useApolloSharedSubscription } from '../../subscription/apollo-subscription.manager';
import { MachineBindingResult } from './binding.types';

export interface RealtimePositionResult {
  getLastPosition(): RealtimePositionEvent | null;
}

export interface RealtimePositionConfig {
  rate?: number;
}

export interface RealtimePositionContext {
  rate?: number;
}

export interface RealtimePositionEvent {
  waypoint: Waypoint;
  jointPositions: QNear;
  tcpSpeed?: number[];
}

const MAX_PREVIOUS_RESULTS = 1;

export const useRealtimePositionMonitorBinding = (
  machineId: string,
  config: RealtimePositionConfig,
  options?: { skip?: boolean }
): RealtimePositionResult => {
  const { rate } = config;

  const previousResults: MutableRefObject<
    Array<MachineBindingResult<RealtimePositionEvent>>
  > = useRef([]);

  const variables: RegisterAndSubscribeToMachineBindingSubscriptionVariables = {
    input: {
      machineId,
      binding: {
        namespace: 'machine',
        type: 'realtimePositionMonitor',
        context: {
          rate: rate ?? 500,
        },
      },
    },
  };

  useApolloSharedSubscription<RegisterAndSubscribeToMachineBindingSubscriptionResult>(
    {
      query: RegisterAndSubscribeToMachineBindingDocument,
      variables,
      subscriptionName: `binding-machine-realtimePositionMonitor=${machineId}`,
      callbackOnly: true,
      skip: options?.skip,
      callback: (
        result: RegisterAndSubscribeToMachineBindingSubscriptionResult
      ) => {
        const bindingResult = result.data
          ?.registerAndSubscribeToMachineBindingResult as MachineBindingResult<RealtimePositionEvent>;

        if (bindingResult) {
          ArrayUtils.unshift(
            previousResults.current,
            bindingResult,
            MAX_PREVIOUS_RESULTS
          );
        }
      },
    }
  );

  const result: RealtimePositionResult = useMemo(() => {
    return {
      getLastPosition: () =>
        previousResults && previousResults.current
          ? previousResults.current[0]?.data
          : null,
    };
  }, []);

  return result;
};
